import { InAppState } from "../base/InAppState";
import template from "./EventsTemplate.html";
import { Events } from "../../core/Events";
import { _lcs } from "../../i18n";

/**
 * EventsState — the "Events" tab: the house's own events and the regional pool
 * with a scope filter (Alle / Vom Haus / Region). The feed comes from
 * core/Events.js (already loaded on boot, refreshed here so a guest who leaves
 * the app open overnight does not see yesterday's list).
 *
 * Filtering is client-side (toggles card visibility) so it stays instant and
 * keeps scroll position.
 */
export class EventsState extends InAppState
{
    static ROUTE = "/events";
    static ID = "events";

    getActiveTab() { return "events"; }

    async enter()
    {
        // Render whatever is already known (usually everything, loaded during
        // boot) and only wait on the network when nothing is there yet.
        if ( !this.app.events.loaded )
        {
            await this.app.events.load();
        }
        else
        {
            this.app.events.load( true ).then( () => this.refresh() );
        }

        if ( this.app.events.showWide )
        {
            this.app.events.loadWide().then( () => this.refresh() );
        }

        this.render();
    }

    render()
    {
        const wide = this.app.events.showWide && !!this.app.events.wideItems;
        const events = this.app.events.getCards( wide );

        // A background refresh re-renders the list — keep the scope the guest
        // picked instead of snapping back to "Alle".
        const activeFilter = this._activeFilter();
        const failed = this.app.events.failed && events.length === 0;

        this.renderScreen( template, {
            filters: EventsState.filtersFor( events ),
            events: events,
            hasEvents: events.length > 0,
            failed: failed,
            // The radius switch: only for a real house (the demo feed has no
            // radius) and not over a feed that failed to load at all.
            canWiden: !this.app.events.isDemo() && !failed,
            wide: wide,
            radiusKm: Events.RADIUS_KM
        } );

        this.bind();

        if ( activeFilter && activeFilter !== "all" )
        {
            this._applyFilter( activeFilter );
        }
    }

    /**
     * Only offer a scope the guest can actually reach. A house may switch the
     * regional pool off entirely (client-admin → Region-Veranstaltungen), and
     * a "Region" chip that always leads to an empty list is a dead end. With a
     * single scope the chip row disappears altogether — there is nothing to
     * choose.
     */
    static filtersFor( events )
    {
        const scopes = new Set( events.map( ( event ) => event.scope ) );

        // German source labels double as their dictionary keys; the template
        // translates them.
        const filters = [
            { id: "own",    label: "Vom Haus" },
            { id: "region", label: "Region" }
        ].filter( ( filter ) => scopes.has( filter.id ) );

        return filters.length > 1 ? [ { id: "all", label: "Alle" } ].concat( filters ) : [];
    }

    _activeFilter()
    {
        const chip = this.app.view.pageContent?.querySelector( ".chip--active" );
        return chip ? chip.getAttribute( "data-filter" ) : null;
    }

    // Re-render after a background refresh, unless the guest has navigated on.
    refresh()
    {
        if ( this.app.stateManager.currentState !== this )
        {
            return;
        }
        this.render();
    }

    bind()
    {
        const root = this.app.view.pageContent;

        root.querySelectorAll( ".chip" ).forEach( ( chip ) =>
        {
            this.addEventListener( chip, "click", () => this._applyFilter( chip.getAttribute( "data-filter" ) ) );
        } );

        root.querySelectorAll( ".eventcard" ).forEach( ( card ) =>
        {
            this.addEventListener( card, "click", () =>
                this.app.router.redirect( "/events/" + card.getAttribute( "data-event" ) ) );
        } );

        const radius = root.querySelector( '[data-id="toggle-radius"]' );
        if ( radius )
        {
            this.addEventListener( radius, "click", () => this._toggleRadius( radius ) );
        }
    }

    /**
     * Widen the list past the guest radius, or narrow it back. The wide feed
     * is fetched on first use only; until it arrives the button says so, and
     * if it never does the list simply stays nearby.
     */
    async _toggleRadius( button )
    {
        const events = this.app.events;

        if ( events.showWide )
        {
            events.showWide = false;
            this.render();
            return;
        }

        button.disabled = true;
        button.textContent = _lcs( "Wird geladen …" );

        if ( !events.wideItems )
        {
            await events.loadWide();
        }
        events.showWide = !!events.wideItems;
        this.refresh();
    }

    _applyFilter( filter )
    {
        const root = this.app.view.pageContent;
        root.querySelectorAll( ".chip" ).forEach( ( c ) =>
            c.classList.toggle( "chip--active", c.getAttribute( "data-filter" ) === filter ) );

        let visible = 0;
        root.querySelectorAll( ".eventcard" ).forEach( ( card ) =>
        {
            const show = filter === "all" || card.getAttribute( "data-scope" ) === filter;
            card.classList.toggle( "is-hidden", !show );
            if ( show ) visible++;
        } );

        // "Nothing under this filter" is a different message from "nothing at all".
        const empty = root.querySelector( '[data-id="filter-empty"]' );
        if ( empty )
        {
            empty.hidden = visible > 0;
        }
    }
}
